import Container from '@/components/feature/Container';
import Reveal from '@/components/base/Reveal';
import Eyebrow from '@/components/base/Eyebrow';
import TextLink from '@/components/base/TextLink';
import Photo from '@/components/base/Photo';
import PillLink from '@/components/base/PillLink';
import CareAccordion from './CareAccordion';

export function Statement() {
  return (
    <section className="mt-16 md:mt-28 bg-accent-500">
      <Container className="py-24 md:py-40">
        <Reveal>
          <h2 className="text-center text-background-50 text-[34px] sm:text-[44px] md:text-[56px] lg:text-[64px] leading-[1.08] font-medium tracking-[-0.02em] max-w-[1050px] mx-auto">
            More than a place to receive care. A place to feel <span className="em-serif">at home.</span>
          </h2>
        </Reveal>
      </Container>
    </section>
  );
}

export function Philosophy() {
  return (
    <section className="py-16 md:py-28">
      <Container>
        <Reveal>
          <Photo
            className="w-full aspect-[4/3] md:aspect-[16/7] lg:aspect-[16/6.5] rounded-2xl md:rounded-[20px]"
            position="object-[center_32%]"
            reveal="right"
            revealVariant="wide"
            alt="Older man reading the newspaper by a sunny window with a cup of tea"
            src="https://public.readdy.ai/ai/img_res/sandbox/d9f57693db1a3f647bcb80a40a4edde0/5fa7b452-069c-4ae4-8f43-36e8851e8c88_dw-phil-01.webp"
          />
        </Reveal>
        <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-[60fr_40fr] gap-6 md:gap-8 lg:gap-14 items-start">
        <Reveal>
          <Eyebrow>Our Philosophy</Eyebrow>
          <h2 className="mt-5 text-[32px] md:text-[46px] lg:text-[56px] leading-[1.06] font-medium tracking-[-0.02em] max-w-[14ch]">
            Everyday life, <span className="em-serif">thoughtfully</span> considered.
          </h2>
        </Reveal>
        <Reveal className="lg:pt-11" delay={100}>
          <p className="text-[16px] md:text-[17px] leading-relaxed text-foreground-700 max-w-md">
            Every person arrives with a lifetime of habits, relationships and preferences. We take the time to learn them &mdash; the morning coffee, the favourite chair, the friends who visit on Sundays &mdash; so support fits around a life, rather than the other way round.
          </p>
          <div className="mt-8"><TextLink to="/our-care">Discover Our Approach</TextLink></div>
        </Reveal>
        </div>
      </Container>
    </section>
  );
}

export function Services() {
  return (
    <section className="py-16 md:py-28 bg-background-100">
      <Container>
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <Eyebrow>Care Services</Eyebrow>
            <h2 className="mt-5 text-[32px] md:text-[46px] leading-[1.08] font-medium tracking-[-0.02em]">
              Support that feels <span className="em-serif">personal.</span>
            </h2>
          </div>
          <TextLink to="/our-care">Explore Our Care</TextLink>
        </Reveal>
        <CareAccordion />
      </Container>
    </section>
  );
}

export function Moments() {
  return (
    <section className="py-16 md:py-28">
      <Container>
        <Reveal>
          <h2 className="text-[32px] md:text-[46px] leading-[1.08] font-medium tracking-[-0.02em] max-w-xl">
            The moments that <span className="em-serif">make a day.</span>
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
          <Reveal className="md:col-span-8">
            <Photo
              className="w-full h-[320px] md:h-[680px] rounded-2xl"
              reveal="down"
              revealGroup="home-moments"
              revealVariant="portrait"
              alt="Two older friends walking arm in arm along a tree-lined path"
              src="https://public.readdy.ai/ai/img_res/sandbox/d9f57693db1a3f647bcb80a40a4edde0/5fa7b452-069c-4ae4-8f43-36e8851e8c88_dw-mom-01.webp"
            />
            <p className="mt-3 text-[13px] text-foreground-600">Morning walks, at whatever pace suits.</p>
          </Reveal>
          <Reveal className="md:col-span-4" delay={100}>
            <div className="flex flex-col gap-4 md:gap-5 md:h-[680px]">
            <Photo
              className="w-full h-[280px] md:h-auto md:flex-1 md:min-h-0 rounded-2xl"
              reveal="down"
              revealGroup="home-moments"
              revealVariant="portrait"
              alt="Older couple playing cards and laughing at a table"
              src="https://public.readdy.ai/ai/img_res/sandbox/d9f57693db1a3f647bcb80a40a4edde0/5fa7b452-069c-4ae4-8f43-36e8851e8c88_dw-mom-02.webp"
            />
            <Photo
              className="w-full h-[280px] md:h-auto md:flex-1 md:min-h-0 rounded-2xl"
              reveal="down"
              revealGroup="home-moments"
              revealVariant="portrait"
              alt="Grandmother and grandchild sharing a book outdoors"
              src="https://public.readdy.ai/ai/img_res/sandbox/d9f57693db1a3f647bcb80a40a4edde0/5fa7b452-069c-4ae4-8f43-36e8851e8c88_dw-mom-03.webp"
            />
            </div>
            <p className="mt-3 text-[13px] text-foreground-600 text-right">Small rituals, shared with people who matter.</p>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

export function Spaces() {
  return (
    <section className="pb-16 md:pb-28">
      <Container>
        <Reveal>
          <div className="relative h-[560px] md:h-[720px] rounded-2xl md:rounded-[20px] overflow-hidden group"
            data-dw-reveal="right"
            data-dw-reveal-variant="wide"
            data-dw-reveal-layer="under"
          >
            <img
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.02]"
              alt="Bright residential lounge with armchairs, bookshelves and garden views"
              title="Daywell Living residential lounge"
              src="https://public.readdy.ai/ai/img_res/sandbox/d9f57693db1a3f647bcb80a40a4edde0/5fa7b452-069c-4ae4-8f43-36e8851e8c88_dw-space-01.webp"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/20 to-transparent" />
            <div className="absolute inset-y-0 left-0 flex items-end md:items-center p-7 md:p-14 lg:p-16">
              <div className="max-w-md text-background-50">
                <Eyebrow light>Our Homes</Eyebrow>
                <h2 className="mt-5 text-[32px] md:text-[48px] leading-[1.05] font-medium tracking-[-0.02em]">
                  Space to feel <span className="em-serif">at home.</span>
                </h2>
                <p className="mt-5 text-[16px] leading-relaxed text-background-50/85">
                  Comfortable rooms, familiar belongings and quiet corners to share with friends &mdash; surroundings designed to feel lived-in and genuinely yours.
                </p>
                <div className="mt-7"><TextLink to="/life-here" light>Explore Life Here</TextLink></div>
              </div>
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

export function VisitCta() {
  return (
    <section className="bg-background-50 pt-16 md:pt-28">
      <Container>
        <Reveal>
          <Eyebrow>An Invitation to Visit</Eyebrow>
          <h2 className="mt-6 text-[42px] sm:text-[60px] md:text-[80px] lg:text-[104px] leading-[0.98] font-medium tracking-[-0.035em] text-foreground-950">
            Come see what<br />life feels like.
          </h2>
        </Reveal>
        <Reveal delay={100}>
          <div className="mt-12 md:mt-20 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <p className="text-[16px] md:text-[18px] leading-relaxed text-foreground-700 max-w-md">A warm welcome, a real conversation, and a place to discover together.</p>
            <PillLink to="/visit#enquiry">Book a Visit &#8599;</PillLink>
          </div>
        </Reveal>
      </Container>
      <Reveal delay={160}>
          <Photo
            className="mt-10 md:mt-14 w-full h-[320px] sm:h-[420px] lg:h-[520px]"
            reveal="right"
            revealVariant="wide"
            alt="Older friends enjoying a quiet golden afternoon in a residential garden"
            src="https://public.readdy.ai/ai/img_res/sandbox/d9f57693db1a3f647bcb80a40a4edde0/5fa7b452-069c-4ae4-8f43-36e8851e8c88_dw-close-01.webp"
          />
      </Reveal>
    </section>
  );
}